import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { store } from "./store";
import type { ComponentMap } from "../types";

const defaultComponentMap: ComponentMap = {
  LanguageSwitcher: {
    import: () =>
      import("../common/features/language/components/LanguageSwitcher"),
  },
  UserActions: {
    import: () => import("../parts/header/UserActions"),
  },
  FixedActionsRight: {
    import: () => import("../parts/footer/FixedActionsRight"),
  },
  FixedActionsLeft: {
    import: () => import("../parts/footer/FixedActionsLeft"),
  },
  MobileBottomNav: {
    import: () => import("../parts/footer/MobileBottomNav"),
  },
  SettingsPopup: {
    import: () => import("../parts/footer/SettingsPopup/SettingsPopup"),
  },
  AppPanels: {
    import: () => import("../parts/footer/AppPanels"),
  },
  GlobalToaster: {
    import: () => import("../common/components/Toaster/GlobalToaster"),
  },
  GlobalModal: {
    import: () =>
      import("../common/features/globalModal/components/GlobalModal"),
  },
  GlobalPopup: {
    import: () =>
      import("../common/features/globalPopup/components/GlobalPopup"),
  },
  ProductsArchive: {
    import: () =>
      import("../templates/ecommerce/product/components/ProductsArchive/ProductsArchive"),
    needsRouter: true,
  },
  ProductSingle: {
    import: () =>
      import("../templates/ecommerce/product/components/ProductSingle/ProductSingle"),
    needsRouter: true,
  },
  ProductModal: {
    import: () =>
      import("../templates/ecommerce/product/components/common/ProductModal/ProductModal"),
  },
  WishlistPage: {
    import: () =>
      import("../templates/ecommerce/wishlist/components/WishlistPage/WishlistPage"),
    needsRouter: true,
  },
  ComparePage: {
    import: () =>
      import("../templates/ecommerce/compare/components/Page/ComparePage"),
    needsRouter: true,
  },
  CartPage: {
    import: () =>
      import("../templates/ecommerce/cart/components/CartPage/CartPage"),
    needsRouter: true,
  },
  CheckoutPage: {
    import: () =>
      import("../templates/ecommerce/checkout/components/CheckoutPage"),
    needsRouter: true,
  },
  OrderPage: {
    import: () => import("../templates/ecommerce/order/components/OrderPage"),
    needsRouter: true,
  },
  AuthForms: {
    import: () => import("../templates/user/components/Auth/AuthForms"),
    needsRouter: true,
  },
  ProfileDashboard: {
    import: () =>
      import("../templates/user/components/Profile/ProfileDashboard"),
    needsRouter: true,
  },
  ForgotPasswordForm: {
    import: () =>
      import("../templates/user/components/Auth/ForgotPasswordForm"),
  },
  ResetPasswordForm: {
    import: () => import("../templates/user/components/Auth/ResetPasswordForm"),
  },
  FeaturedProductsSlider: {
    import: () => import("../partials/PageContent/noc/FeaturedProductsSlider"),
  },
  MegaMenu: {
    import: () => import("../parts/header/MegaMenu"),
  },
  ContactForm: {
    import: () => import("../templates/contact/components/ContactForm"),
  },
  FranchiseForm: {
    import: () => import("../templates/franchise/components/FranchiseForm"),
  },
  CatalogueForm: {
    import: () =>
      import("../partials/PageContent/noc/catalogueForm/components/CatalogueForm"),
  },
  CookieBanner: {
    import: () =>
      import("../common/features/cookieConsent/components/CookieBanner"),
  },
  RecentlyViewed: {
    import: () =>
      import("../partials/PageContent/noc/recentlyViewed/components/RecentlyViewed"),
  },
};

const getComponentMap = (): ComponentMap => {
  const themeOverrides = window.ThemeComponentOverrides || {};

  if (Object.keys(themeOverrides).length > 0) {
    console.log(
      `[Hydrate] Applying ${Object.keys(themeOverrides).length} theme override(s).`,
    );
  }

  // Spread overrides AFTER default map to allow replacing core components
  return { ...defaultComponentMap, ...themeOverrides };
};

export const hydrateComponents = (): void => {
  const containers = document.querySelectorAll<HTMLElement>(
    "[data-hydrate-component]",
  );
  if (containers.length === 0) return;

  const componentMap = getComponentMap();

  containers.forEach((container) => {
    const componentName = container.dataset.hydrateComponent;
    if (componentName && componentMap[componentName]) {
      const config = componentMap[componentName];

      config
        .import()
        .then((module) => {
          const Component = module.default;
          const root = createRoot(container);
          const props: Record<string, string> = {};

          Object.keys(container.dataset).forEach((key) => {
            if (key !== "hydrateComponent")
              props[key] = container.dataset[key] || "";
          });

          root.render(
            <React.StrictMode>
              <Provider store={store}>
                {config.needsRouter ? (
                  <BrowserRouter>
                    <Component {...props} />
                  </BrowserRouter>
                ) : (
                  <Component {...props} />
                )}
              </Provider>
            </React.StrictMode>,
          );
        })
        .catch((err) =>
          console.error(`[Hydrate] Error loading ${componentName}:`, err),
        );
    }
  });
};
